import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useEffect, useMemo, useState } from 'react';
import {
  createBillingPortalSession,
  createCheckoutSession,
} from '../services/appStateService';
import {
  AGENCY_PLAN_IDS,
  NUDGE_PLANS,
  normalizeFrontendPlanId,
} from '../lib/nudgePlanLimits';

const LAUNCH_OFFER_CODES = new Set([
  'NUDGE-PH26',
  'PRODUCTHUNT',
  'PH',
  'PRODUCTHUNT2026',
  'SNOOOZ',
  'SNOOOZAI',
  'SNOOOZUSER',
  'SNOOOZ-NUDGE-26',
].map(normalizeLaunchCode));

function normalizeLaunchCode(value = '') {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 24);
}

function normalizeStripePromoCode(value = '') {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, 64);
}

function isLaunchOfferCode(value = '') {
  return LAUNCH_OFFER_CODES.has(normalizeLaunchCode(value));
}

function lookbackLabel(days = 0) {
  const numeric = Number(days || 0);
  if (numeric >= 1460) return '4 years';
  if (numeric >= 1095) return '3 years';
  if (numeric >= 730) return '2 years';
  if (numeric >= 365) return '1 year';
  if (numeric >= 180) return '6 months';
  if (numeric >= 90) return '3 months';
  if (numeric >= 30) return '30 days';
  return `${numeric} days`;
}

function planFeatureLines(planId) {
  const plan = NUDGE_PLANS[planId] || NUDGE_PLANS.pro_2026;

  const lines = [
    `${plan.mailboxes} connected mailbox${Number(plan.mailboxes) === 1 ? '' : 'es'}`,
    `${Number(plan.followUpCreditsMonthly || 0).toLocaleString()} follow-up drafts per month`,
    `${lookbackLabel(plan.historicalLookbackDays)} historical lookback`,
  ];

  if (plan.teamManagement) {
    lines.push(
      `${plan.unlimitedWorkspaces ? 'Unlimited' : plan.workspaces} workspace${plan.unlimitedWorkspaces || Number(plan.workspaces) !== 1 ? 's' : ''}`,
      `${plan.unlimitedTeamMembers ? 'Unlimited' : plan.teamMembers} team member${plan.unlimitedTeamMembers || Number(plan.teamMembers) !== 1 ? 's' : ''}`,
    );
  }

  if (plan.allowContinueUntilReply) lines.push('Continue follow-up sequences until reply');
  if (plan.autoSend) lines.push('Optional automatic sending');
  if (plan.researchAndDraft) lines.push('Research & Draft + enrichment providers');
  if (plan.includeThreadAttachments) lines.push('Reuse previous attachments when supported');

  return lines;
}

function planPrice(planId, interval) {
  const plan = NUDGE_PLANS[planId] || {};
  return interval === 'monthly'
    ? Number(plan.monthlyPrice || 0)
    : Number(plan.annualPrice || 0);
}

function PlanChoice({
  selected,
  title,
  eyebrow,
  price,
  interval,
  description,
  onClick,
  children,
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick?.();
      }}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'rgba(255,182,72,.08)' : '#fff',
        cursor: 'pointer',
        transition: 'all .18s ease',
        '&:hover': {
          borderColor: selected ? 'primary.main' : 'rgba(26,29,49,.25)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            {eyebrow}
          </Typography>
          <Typography variant="h3" sx={{ fontSize: 22, mt: .25 }}>
            {title}
          </Typography>
        </Box>
        {selected && <Chip size="small" color="primary" label="Selected" />}
      </Stack>

      <Typography sx={{ mt: 1.4, fontWeight: 900, fontSize: 28, lineHeight: 1 }}>
        ${price.toLocaleString()}
        <Typography component="span" color="text.secondary" sx={{ fontSize: 13, fontWeight: 700 }}>
          {interval === 'monthly' ? ' / month' : ' / year'}
        </Typography>
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description}
      </Typography>

      {children}
    </Box>
  );
}

export function UpgradeModal({
  open,
  onClose,
  reason = '',
  subscription = null,
  launchRef = '',
  onUpgraded,
}) {
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [interval, setInterval] = useState('annual');
  const [selectedPlanId, setSelectedPlanId] = useState('pro_2026');

  const currentPlanId = normalizeFrontendPlanId(subscription?.planId || 'free');
  const currentStatus = String(subscription?.status || '').toLowerCase();
  const currentSource = String(subscription?.source || '').toLowerCase();

  const hasStripeSubscription = Boolean(
    currentSource === 'stripe' ||
    subscription?.stripeSubscriptionId ||
    subscription?.stripe?.subscriptionId,
  );

  const isLifetimePlan = Boolean(
    subscription?.lifetime ||
    currentPlanId.startsWith('appsumo_tier_'),
  );

  const isLegacyPlan = ['legacy_pro', 'pro_ph_trial'].includes(currentPlanId);
  // Product Hunt/Snoooz launch-trial accounts remain eligible for the original
  // Pro price even after the effective trial plan falls back to Free.
  const isGrandfatheredLaunchAccount = currentSource === 'product_hunt';
  const isLegacyTrial = isGrandfatheredLaunchAccount ||
    currentPlanId === 'pro_ph_trial' ||
    (isLegacyPlan && currentStatus === 'trialing');

  const manualCode = normalizeStripePromoCode(couponCode);
  const urlLaunchRef = normalizeLaunchCode(launchRef);

  // URL/referral values are ONLY launch offers if they match a known launch code.
  // They must never be treated as Stripe promo codes.
  const launchCodeFromUrl = isLaunchOfferCode(urlLaunchRef)
    ? urlLaunchRef
    : '';

  const launchCodeFromManual = isLaunchOfferCode(manualCode)
    ? manualCode
    : '';

  const hasLaunchOffer = Boolean(
    launchCodeFromManual || launchCodeFromUrl
  );

  const grandfatheredCheckout = isLegacyTrial || hasLaunchOffer;
  const proCheckoutPlanId = grandfatheredCheckout ? 'legacy_pro' : 'pro_2026';

  useEffect(() => {
    if (!open) return;

    setError('');
    setLoading('');
    setInterval('annual');

    if (grandfatheredCheckout) {
      setSelectedPlanId('legacy_pro');
    } else {
      setSelectedPlanId('pro_2026');
    }
  }, [open, grandfatheredCheckout]);

  const selectedPlan = NUDGE_PLANS[selectedPlanId] || NUDGE_PLANS.pro_2026;
  const selectedPrice = planPrice(selectedPlanId, interval);

  const agencyPlan = useMemo(() => {
    const id = AGENCY_PLAN_IDS.includes(selectedPlanId)
      ? selectedPlanId
      : 'agency_6';
    return NUDGE_PLANS[id];
  }, [selectedPlanId]);

  async function openBillingPortal() {
    setError('');
    setLoading('portal');

    try {
      const result = await createBillingPortalSession();

      if (result?.url) {
        window.location.assign(result.url);
        return;
      }

      setError('Could not open billing. Please try again.');
    } catch (err) {
      setError(err?.message || 'Could not open billing.');
    } finally {
      setLoading('');
    }
  }

  async function startCheckout() {
    const launchCode =
      launchCodeFromManual ||
      launchCodeFromUrl ||
      '';

    // Only a code explicitly typed into the coupon field can become
    // a Stripe promotion code.
    const stripeCoupon =
      manualCode && !isLaunchOfferCode(manualCode)
        ? manualCode
        : '';

    // Product Hunt / Snoooz launch trials and existing Product Hunt trial users
    // intentionally remain on the original Legacy Pro Stripe price.
    const targetPlanId = grandfatheredCheckout
      ? 'legacy_pro'
      : selectedPlanId;

    setError('');
    setLoading(`checkout:${targetPlanId}:${interval}`);

    try {
      const result = await createCheckoutSession({
        planId: targetPlanId,
        interval,
        launchRef: launchCode,
        couponCode: stripeCoupon,
      });

      if (result?.url) {
        window.location.assign(result.url);
        return;
      }

      setError('Could not open checkout. Please try again.');
    } catch (err) {
      setError(err?.message || 'Could not open checkout.');
    } finally {
      setLoading('');
    }
  }

  const legacyPrice = planPrice('legacy_pro', interval);
  const proPrice = planPrice(proCheckoutPlanId, interval);
  const isAgencySelected = AGENCY_PLAN_IDS.includes(selectedPlanId);

  // Existing Stripe subscriptions should be changed through Stripe Billing
  // rather than creating a second subscription.
  if (hasStripeSubscription) {
    const label = subscription?.limits?.label || NUDGE_PLANS[currentPlanId]?.label || 'Paid plan';

    return (
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="h3" sx={{ fontSize: 24 }}>Manage {label}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {isLegacyPlan && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Your account is grandfathered. Your existing Legacy Pro pricing and entitlements are preserved.
            </Alert>
          )}

          <Typography color="text.secondary">
            Your subscription is already managed by Stripe. Open the billing portal to manage billing, cancellation,
            payment details, or any plan changes enabled for your account.
          </Typography>

          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 3 }}
            disabled={Boolean(loading)}
            onClick={openBillingPortal}
          >
            {loading === 'portal' ? 'Opening…' : 'Manage billing'}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Lifetime users should not accidentally create a second paid subscription.
  if (isLifetimePlan) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Typography variant="h3" sx={{ fontSize: 24 }}>Your lifetime plan is active</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info">
            {reason || 'This feature is outside the limits of your current lifetime tier.'}
          </Alert>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Lifetime tiers are managed separately from public subscriptions, so Nudge will not create a second
            subscription for this account from the upgrade dialog.
          </Typography>
          <Button fullWidth variant="outlined" sx={{ mt: 3 }} onClick={onClose}>Close</Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,179,71,.18)',
              color: 'primary.dark',
            }}
          >
            <AutoAwesomeRoundedIcon />
          </Box>

          <Box>
            <Typography variant="h3" sx={{ fontSize: 25, mb: .5 }}>
              {grandfatheredCheckout ? 'Keep your Legacy Pro pricing' : 'Choose the plan that fits your follow-up workload'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reason || 'Upgrade mailbox capacity, automation, team access, and research when you need them.'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {grandfatheredCheckout && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This account qualifies for the original Pro pricing. Your Pro checkout remains ${12}/month or ${96}/year.
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <Typography fontWeight={900}>Billing</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={interval}
            onChange={(_, value) => value && setInterval(value)}
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annual">Annual · save</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Box sx={{ flex: 1 }}>
            <PlanChoice
              selected={!isAgencySelected}
              title={grandfatheredCheckout ? 'Legacy Pro' : 'Pro'}
              eyebrow={grandfatheredCheckout ? 'Grandfathered' : 'For individual follow-up'}
              price={grandfatheredCheckout ? legacyPrice : proPrice}
              interval={interval}
              description={
                grandfatheredCheckout
                  ? 'Your original Pro plan and pricing stay available to this account.'
                  : 'A focused plan for founders, salespeople, consultants, and small teams.'
              }
              onClick={() => setSelectedPlanId(proCheckoutPlanId)}
            >
              <Stack spacing={.65} sx={{ mt: 1.5 }}>
                {planFeatureLines(proCheckoutPlanId).slice(0, 6).map((item) => (
                  <Stack key={item} direction="row" spacing={.8} alignItems="flex-start">
                    <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18, mt: .1 }} />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </PlanChoice>
          </Box>

          {!grandfatheredCheckout && (
            <Box sx={{ flex: 1 }}>
              <PlanChoice
                selected={isAgencySelected}
                title="Agency"
                eyebrow="Teams, clients & multiple inboxes"
                price={planPrice(isAgencySelected ? selectedPlanId : 'agency_6', interval)}
                interval={interval}
                description="Scale Nudge across workspaces, teammates, research, auto-send, and larger follow-up pipelines."
                onClick={() => setSelectedPlanId(isAgencySelected ? selectedPlanId : 'agency_6')}
              >
                <Box
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  sx={{ mt: 1.5 }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>
                    Mailbox capacity
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    sx={{ mt: .6 }}
                    value={isAgencySelected ? selectedPlanId : 'agency_6'}
                    onChange={(event) => setSelectedPlanId(event.target.value)}
                  >
                    {AGENCY_PLAN_IDS.map((id) => {
                      const plan = NUDGE_PLANS[id];
                      return (
                        <MenuItem key={id} value={id}>
                          {plan.mailboxes} mailboxes · ${planPrice(id, interval).toLocaleString()}
                          {interval === 'monthly' ? '/mo' : '/yr'}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>

                <Stack spacing={.65} sx={{ mt: 1.5 }}>
                  {planFeatureLines(isAgencySelected ? selectedPlanId : 'agency_6').slice(0, 8).map((item) => (
                    <Stack key={item} direction="row" spacing={.8} alignItems="flex-start">
                      <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18, mt: .1 }} />
                      <Typography variant="body2">{item}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </PlanChoice>
            </Box>
          )}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={800} sx={{ mb: .7 }}>
            Have a launch or coupon code?
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={couponCode}
            onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
            placeholder="Enter launch or promo code"
            inputProps={{ maxLength: 64 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: .8 }}>
            Launch codes keep their original offer rules. Standard promo codes are validated in Stripe Checkout.
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={Boolean(loading)}
          onClick={startCheckout}
        >
          {loading
            ? 'Opening checkout…'
            : hasLaunchOffer
              ? `Start 60-day Pro trial · then $${legacyPrice.toLocaleString()}${interval === 'monthly' ? '/month' : '/year'}`
              : `Continue with ${isAgencySelected ? `${selectedPlan.mailboxes}-mailbox Agency` : selectedPlan.label} · $${selectedPrice.toLocaleString()}${interval === 'monthly' ? '/month' : '/year'}`}
        </Button>

        {!grandfatheredCheckout && interval === 'annual' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2, textAlign: 'center' }}>
            Annual pricing reflects the public yearly plan. You can manage billing later from Settings.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
